// eslint-disable-next-line max-classes-per-file
import uniq from 'lodash/uniq';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { dateFormatConfig, defaultDateFormat } from './dateFormatting';
import { timeFormatConfig, defaultTimeFormat } from './timeFormatting';

export class InvalidDateError extends Error {
  constructor(message) {
    super(message);
    this.name = 'InvalidDateError';
  }
}

export class InvalidDateTimeError extends Error {
  constructor(message) {
    super(message);
    this.name = 'InvalidDateTimeError';
  }
}

const yesplanAPIDateFormat = 'YYYY-MM-DD';
const yesplanAPIDateTimeFormat = `${yesplanAPIDateFormat}THH:mm:ssZ`;
const yesplanAPITimeFormat = 'HH:mm';

/* Always use this function instead of the moment function from the library,
   because this one enforces the strict parsing */
export const toYesplanDateTimeObject = (yesplanDateAndOrTimeString) =>
  moment(yesplanDateAndOrTimeString, moment.ISO_8601, true);

export const todayDateObject = () => moment();

/* Serialization and deserialization: from ISO format->moment and vice versa. */
export const deserializeYesplanAPIDate = (yesplanAPIDateString) => {
  const dateObject = toYesplanDateTimeObject(yesplanAPIDateString);
  if (!dateObject.isValid())
    throw new InvalidDateError(
      `Called deserializeYesplanAPIDate with invalid date: ${yesplanAPIDateString}`
    );
  return dateObject;
};

/* Strict ISO parsing that keeps the offset the API sent - moment.parseZone,
   not moment (see the warning further down). Shared by the throwing and the
   non-throwing deserializer below. */
const parsedYesplanAPIDateAndTime = (yesplanAPIDateTimeString) =>
  moment.parseZone(yesplanAPIDateTimeString, moment.ISO_8601, true);

export const deserializeYesplanAPIDateAndTime = (yesplanAPIDateTimeString) => {
  const dateAndTimeObject = parsedYesplanAPIDateAndTime(yesplanAPIDateTimeString);
  if (!dateAndTimeObject.isValid())
    throw new InvalidDateTimeError(
      `Called deserializeYesplanAPIDateTime with invalid date+time: ${yesplanAPIDateTimeString}`
    );
  return dateAndTimeObject;
};

/* deserializeYesplanAPIDateAndTime for a value that is not known to be a
 * date&time for sure */
export const deserializeYesplanAPIDateAndTimeOrNull = (value) => {
  if (typeof value !== 'string') return null;
  const dateAndTimeObject = parsedYesplanAPIDateAndTime(value);
  return dateAndTimeObject.isValid() ? dateAndTimeObject : null;
};

export const deserializeYesplanAPITime = (yesplanAPITimeString) => {
  const timeObject = moment(yesplanAPITimeString, yesplanAPITimeFormat, true);
  if (!timeObject.isValid())
    throw new InvalidDateTimeError(
      `Called deserializeYesplanAPITime with invalid date+time: ${yesplanAPITimeString}`
    );
  return timeObject;
};

export const deserializeAndGetTypeYesplanDateAndOrTime = (
  yesplanAPIDateAndOrTimeString
) => {
  /* Use the fact that you can pass multiple formats to moment, and how it uses the one that returns a valid moment object:
    https://momentjs.com/docs/#/parsing/string-formats/  */
  const dateObjectDatePart = moment(
    yesplanAPIDateAndOrTimeString,
    [yesplanAPIDateFormat, moment.ISO_8601],
    true
  );
  const dateObjectTimePart = moment(
    yesplanAPIDateAndOrTimeString,
    [
      yesplanAPITimeFormat,
      yesplanAPIDateTimeFormat,
      `${yesplanAPIDateFormat}THH:mm` // Custom data date + time
    ], // can't have moment.ISO_8601, otherwise a date only will be valid
    true
  );
  if (dateObjectDatePart.isValid() && dateObjectTimePart.isValid())
    return {
      isDate: true,
      isTime: true,
      dateObject: deserializeYesplanAPIDateAndTime(yesplanAPIDateAndOrTimeString)
    };
  if (dateObjectDatePart.isValid())
    return {
      isDate: true,
      isTime: false,
      dateObject: deserializeYesplanAPIDate(yesplanAPIDateAndOrTimeString)
    };
  return {
    isDate: false,
    isTime: true,
    dateObject: deserializeYesplanAPITime(yesplanAPIDateAndOrTimeString)
  };
};

export const serializeYesplanAPIDate = (dateObject) => {
  if (!(dateObject instanceof moment) || !dateObject.isValid())
    throw new InvalidDateError(`Called serializeYesplanAPIDate with invalid dateObject`);
  return dateObject.format(yesplanAPIDateFormat);
};

export const serializeYesplanAPIDateRange = ({ startDateObject, endDateObject }) => ({
  from: serializeYesplanAPIDate(startDateObject),
  to: serializeYesplanAPIDate(endDateObject)
});

export const serializeYesplanAPIDateAndTime = (dateTimeObject) => {
  if (!(dateTimeObject instanceof moment) || !dateTimeObject.isValid())
    throw new InvalidDateTimeError(
      `Called serializeYesplanAPIDateAndTime with invalid dateTimeObject`
    );
  return dateTimeObject.format(yesplanAPIDateTimeFormat);
};

export const serializeYesplanAPITime = (dateTimeObject) => {
  if (!(dateTimeObject instanceof moment) || !dateTimeObject.isValid())
    throw new InvalidDateTimeError(
      `Called serializeYesplanAPITime with invalid dateTimeObject`
    );
  return dateTimeObject.format(yesplanAPITimeFormat);
};

export const isDateObjectOfCorrectObjectType = (dateObject) =>
  dateObject instanceof moment;

/* _________________________________________________________________________  */

function getConfiguredFormatForKey(config, key, formatSetting, defaultFormat) {
  if (config[formatSetting] && config[formatSetting][key])
    return config[formatSetting][key];
  return config[defaultFormat][key];
}

export function getConfiguredDateFormatForKey(formatKey, dateFormatSetting) {
  if (dateFormatSetting === null) console.error('No date format');
  const format = dateFormatSetting || defaultDateFormat;
  return getConfiguredFormatForKey(
    dateFormatConfig,
    formatKey,
    format,
    defaultDateFormat
  );
}

export function getConfiguredTimeFormatForKey(formatKey, timeFormatSetting) {
  if (timeFormatSetting === null) console.error('No time format');
  const format = timeFormatSetting || defaultTimeFormat;
  return getConfiguredFormatForKey(
    timeFormatConfig,
    formatKey,
    format,
    defaultTimeFormat
  );
}

/* ⚠️ When parsing date&time or time strings, use moment.parseZone(string) instead of moment(string).
 *  The latter will convert the time from the offset (+hh:mm) to the local or UTC time */

/* Not affected by set date/time format */
export const internalDateFormat = yesplanAPIDateFormat;
export const deprecatedBEDateFormat = 'DD-MM-YYYY';

/* Re-exported so date handling keeps going through this module (the server
   builds query dates too, so the helper itself lives in shared/) */
export { inQueryLanguageDateFormat } from '../../shared/yesplanQueryLanguage';

/* Returns the configured format as a string */
export function getConfiguredDateInputFormat(dateFormatSetting) {
  return getConfiguredDateFormatForKey('dateFormatInput', dateFormatSetting);
}

export function withYesplanDayStartEndDo(dateObject, daylineString, doFunction) {
  /* This date is used to compare start & end times, does not need to be affected by set date/time format */
  const dayStart = moment(
    `${serializeYesplanAPIDate(dateObject)} ${daylineString}`,
    `${yesplanAPIDateFormat} HH:mm`
  );
  const dayEnd = moment(
    `${serializeYesplanAPIDate(dateObject.clone().add(1, 'days'))} ${daylineString}`,
    `${yesplanAPIDateFormat} HH:mm`
  );
  return doFunction(dayStart, dayEnd);
}

export function durationIsNotZero(duration) {
  const momentDuration = moment.duration(duration);
  return momentDuration.asMilliseconds() > 0;
}

export function durationInRosterHourDisplayFormat(duration) {
  const momentDuration = moment.duration(duration);
  const hoursAndMinutesDecimal = momentDuration.asHours().toString();
  /* Perform our own formatting because formatting a duration returns the hours in the range of [0 - 23] */
  const hours = hoursAndMinutesDecimal.includes('.')
    ? hoursAndMinutesDecimal.slice(0, hoursAndMinutesDecimal.indexOf('.'))
    : hoursAndMinutesDecimal;
  const minutesString = moment.utc(momentDuration.as('milliseconds')).format('mm');
  if (minutesString === '00') {
    return i18n('{hh}h', {
      hh: hours
    });
  }
  return i18n('{hh}h{mm}m', {
    hh: hours,
    mm: minutesString
  });
}

export function durationInLongDisplayFormat(duration) {
  const momentDuration = moment.duration(duration);
  const hoursAndMinutesDecimal = momentDuration.asHours().toString();
  /* Perform our own formatting because formatting a duration returns the hours in the range of [0 - 23] */
  const hours = hoursAndMinutesDecimal.includes('.')
    ? hoursAndMinutesDecimal.slice(0, hoursAndMinutesDecimal.indexOf('.'))
    : hoursAndMinutesDecimal;
  const minutesString = moment.utc(momentDuration.as('milliseconds')).format('mm');
  return i18n('{hh}:{mm}', {
    hh: hours,
    mm: minutesString
  });
}

export function timeDifferenceInDays(dateObject1, dateObject2) {
  return Math.ceil(
    dateObject1.clone().startOf('day').diff(dateObject2.clone().startOf('day'), 'd', true)
  );
}

export function timeDifferenceInHours(dateObject1, dateObject2) {
  return moment.duration(dateObject1.diff(dateObject2)).asHours();
}

export function timeDifferenceInMinutes(dateObject1, dateObject2) {
  return moment.duration(dateObject1.diff(dateObject2)).asMinutes();
}

/* Returns moment date */
export function isToday(dateObject) {
  return moment().isSame(moment(dateObject), 'd');
}

export function isOnSameDate(date1, date2) {
  const dateObject1 = moment(date1);
  const dateObject2 = moment(date2);
  /* Documentation of momentjs advices to not use isDST, so use this to detect a change in
     timezones due to a DST change */
  if (Math.abs(dateObject1.utcOffset() - dateObject2.utcOffset()) === 60)
    return (
      dateObject1.clone().startOf('d').format(internalDateFormat) ===
      dateObject2.clone().startOf('d').format(internalDateFormat)
    );
  return moment(dateObject1).isSame(moment(dateObject2), 'd');
}

export function getWeekDaysBetween(weekStart, weekEnd) {
  const days = [weekStart];
  let nextDay = moment(weekStart).add(1, 'd');
  while (moment(nextDay).isBefore(weekEnd)) {
    days.push(nextDay);
    nextDay = moment(nextDay).add(1, 'd');
  }
  return days;
}

/* Does not return weekdays when they are in the future.
   Includes today. */
export function getWeekDaysBetweenNotInFuture(weekStart, weekEnd) {
  const today = todayDateObject();
  let endOfPeriod = weekEnd;
  if (weekStart.isAfter(today)) return [];
  if (weekEnd.isAfter(today)) endOfPeriod = today;

  return getWeekDaysBetween(weekStart, endOfPeriod);
}

/* Returns moment date */
export function getTodayAtMidnight() {
  return moment().hours(0).minutes(0).seconds(0);
}

export function getDateWithDaylineSet(dateObject, daylineString) {
  return moment(
    `${serializeYesplanAPIDate(dateObject)} ${daylineString}`,
    `${yesplanAPIDateFormat} ${yesplanAPITimeFormat}`
  );
}

export function getDateAndTimeWithHoursAndMinutesSet(dateTimeObject, fromDateAndTime) {
  return dateTimeObject
    .clone()
    .hour(fromDateAndTime.hours())
    .minute(fromDateAndTime.minutes());
}

/* Returns object containing start & end of week as moment dates */
export function getWeekOf(dateObject) {
  return {
    startDateObject: moment(dateObject).startOf('isoWeek'),
    endDateObject: moment(dateObject).endOf('isoWeek')
  };
}

/* Returns object containing start & end of week as moment dates */
export function getWeekBefore(week) {
  const newStart = moment(week.startDateObject).add(-7, 'd');
  return getWeekOf(newStart);
}

/* Returns object containing start & end of week as moment dates */
export function getWeekAfter(week) {
  const newStart = moment(week.startDateObject).add(7, 'd');
  return getWeekOf(newStart);
}

/* Returns weeknumber */
export function getWeekNumber(week) {
  return moment(week.startDateObject).isoWeek();
}

/* Return year of start of the week */
export function getYearOfWeek(week) {
  return moment(week.startDateObject).isoWeekYear();
}

/* Returns boolean */
export function isDayPartOfWeek(day, week) {
  return (
    moment(week.startDateObject).isSame(day, 'd') ||
    moment(week.endDateObject).isSame(day, 'd') ||
    (moment(day).isAfter(week.startDateObject, 'd') &&
      moment(day).isBefore(week.endDateObject, 'd'))
  );
}

/* Returns the inputted string in the same format */
export function getMinimumDateTime(datetime1, datetime2) {
  const dateObject1 = moment(datetime1);
  const dateObject2 = moment(datetime2);
  const min = moment.min(dateObject1, dateObject2);
  if (min.isSame(dateObject1)) return datetime1;
  return datetime2;
}

export function compareEventStartTimes(event1, event2) {
  const starttime1 =
    event1.defaultschedulestart !== null ? event1.defaultschedulestart : event1.starttime;
  const starttime2 =
    event2.defaultschedulestart !== null ? event2.defaultschedulestart : event2.starttime;

  if (starttime1) {
    if (starttime2) {
      return moment(starttime1).diff(moment(starttime2));
    }
    return -1;
  }
  if (starttime2) {
    return 1;
  }
  return 0;
}

export function getMonthNames() {
  return [...Array(12).keys()].map((i) => {
    const monthName = moment(i + 1, 'MM').format('MMMM');
    return monthName.charAt(0).toUpperCase() + monthName.slice(1);
  });
}

export const replaceDateSeparator = (typedDate, dateFormatSetting) => {
  const dateSeparatorFormatted = getConfiguredDateFormatForKey(
    'dateSeperator',
    dateFormatSetting
  );
  let typedDateSeparatorFormatted = typedDate;
  const regExpsToReplace = [
    ...uniq(Object.values(dateFormatConfig)).map(
      (dateConfig) => new RegExp(dateConfig.dateSeperator, 'g')
    ),
    /\s+/g,
    /\./g
  ];
  regExpsToReplace.forEach((regExp) => {
    typedDateSeparatorFormatted = typedDateSeparatorFormatted.replace(
      regExp,
      dateSeparatorFormatted
    );
  });
  return typedDateSeparatorFormatted;
};

export const isDateAndTimeSameOrAfter = (dateAndTime1, dateAndTime2) =>
  dateAndTime1.isSameOrAfter(dateAndTime2, 'minute');

export const isDateAndTimeSameOrBefore = (dateAndTime1, dateAndTime2) =>
  dateAndTime1.isSameOrBefore(dateAndTime2, 'minute');
export const isDateAndTimeBefore = (dateAndTime1, dateAndTime2) =>
  dateAndTime1.isBefore(dateAndTime2, 'minute');

export const isDateAndTimeAfter = (dateAndTime1, dateAndTime2) =>
  dateAndTime1.isAfter(dateAndTime2, 'minute');

/* Our own proptype for date objects (moment) */
export const YPDateTimeObjectPropType = (props, propName, componentName) => {
  if (props[propName] === null || props[propName] === undefined) return null;
  if (!(props[propName] instanceof moment)) {
    return new Error(
      `Invalid prop ${propName} passed to ${componentName}. Expected a valid date+time object.`
    );
  }
};
